import { AxiosResponse } from "axios";
import { call, put, select, takeLatest } from "redux-saga/effects";
import type { IntegrationTableColumn } from "../../slices/integration-status/integrationStatusColumns";
import type {
  IntegrationPlatformCard,
  IntegrationSummaryTile,
} from "../../../pages/hid-admin/dashboard/partner-organizations/partner-list/integration-status/integrationStatusTypes";
import { axiosRestInstance } from "../../../common/axios";
import { integrationStatusSliceAction } from "../../slices/integration-status";
import type {
  GetPartnerIntegrationStatusPayload,
  PartnerIntegrationStatusAPIResponse,
} from "../../slices/integration-status/Types";
import { mapPartnerIntegrationStatusResponse } from "../../slices/integration-status/integrationStatusMapper";
import { shouldPreserveDashboardStats } from "../../../pages/hid-admin/dashboard/partner-organizations/partner-list/integration-status/buildIntegrationStatusRequest";
import {
  exportPartnerIntegrationStatusSagaAction,
  getPartnerIntegrationStatusSagaAction,
  integrationStatusSagaActionTypes,
} from "./saga-actions";

const buildIntegrationStatusBody = (
  payload: GetPartnerIntegrationStatusPayload
): GetPartnerIntegrationStatusPayload => {
  const body: GetPartnerIntegrationStatusPayload = {
    pagination: payload.pagination,
  };

  if (payload.search && Object.keys(payload.search).length > 0) {
    body.search = payload.search;
  }

  if (payload.filters && Object.keys(payload.filters).length > 0) {
    body.filters = payload.filters;
  }

  return body;
};

const getPartnerIntegrationStatusAPI = async (
  payload: GetPartnerIntegrationStatusPayload
) => {
  const response = await axiosRestInstance
    .post<AxiosResponse<PartnerIntegrationStatusAPIResponse>>(
      "/project/partner-integration-status",
      buildIntegrationStatusBody(payload)
    )
    .then((res) => res.data);
  return response;
};

const exportPartnerIntegrationStatusAPI = async (
  payload: GetPartnerIntegrationStatusPayload
) => {
  const response = await axiosRestInstance.post<Blob>(
    "/project/partner-integration-status/export",
    buildIntegrationStatusBody(payload),
    { responseType: "blob" }
  );
  return response;
};

const getFileNameFromContentDisposition = (
  contentDisposition: string | undefined,
  fallback = "partner-integration-status.xlsx"
): string => {
  if (!contentDisposition) return fallback;
  const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i.exec(contentDisposition);
  if (!match) return fallback;
  const raw = match[1].replace(/['"]/g, "");
  try {
    return decodeURIComponent(raw) || fallback;
  } catch {
    return raw || fallback;
  }
};

type IntegrationStatusSagaState = {
  integration_status: {
    tableColumns: IntegrationTableColumn[];
    platformCards: IntegrationPlatformCard[];
    summaryTiles: IntegrationSummaryTile[];
  };
};

function* getPartnerIntegrationStatusSaga(
  action: ReturnType<typeof getPartnerIntegrationStatusSagaAction>
) {
  try {
    const hasExistingData: boolean = yield select(
      (state: IntegrationStatusSagaState) =>
        state.integration_status.summaryTiles.length > 0
    );

    yield put(integrationStatusSliceAction.setIntegrationStatusLoading(true));
    yield put(integrationStatusSliceAction.setIntegrationStatusError(null));

    const response: PartnerIntegrationStatusAPIResponse = yield call(
      getPartnerIntegrationStatusAPI,
      action.payload
    );

    if (response?.success) {
      const existingTableColumns: IntegrationTableColumn[] = yield select(
        (state: IntegrationStatusSagaState) => state.integration_status.tableColumns
      );
      const existingPlatformCards: IntegrationPlatformCard[] = yield select(
        (state: IntegrationStatusSagaState) => state.integration_status.platformCards
      );
      const existingSummaryTiles: IntegrationSummaryTile[] = yield select(
        (state: IntegrationStatusSagaState) => state.integration_status.summaryTiles
      );
      const mapped = mapPartnerIntegrationStatusResponse(
        response,
        existingTableColumns
      );
      if (shouldPreserveDashboardStats(action.payload)) {
        if (existingSummaryTiles.length > 0) {
          mapped.summaryTiles = existingSummaryTiles;
        }
        if (existingPlatformCards.length > 0) {
          mapped.platformCards = existingPlatformCards;
        }
      }
      yield put(integrationStatusSliceAction.setIntegrationStatusData(mapped));
    } else {
      if (!hasExistingData) {
        yield put(integrationStatusSliceAction.clearIntegrationStatusData());
      }
      yield put(
        integrationStatusSliceAction.setIntegrationStatusError(
          "Failed to load integration status."
        )
      );
    }
  } catch (error) {
    const hasExistingData: boolean = yield select(
      (state: IntegrationStatusSagaState) =>
        state.integration_status.summaryTiles.length > 0
    );
    if (!hasExistingData) {
      yield put(integrationStatusSliceAction.clearIntegrationStatusData());
    }
    yield put(
      integrationStatusSliceAction.setIntegrationStatusError(
        error instanceof Error ? error.message : "Failed to load integration status."
      )
    );
  } finally {
    yield put(integrationStatusSliceAction.setIntegrationStatusLoading(false));
  }
}

function* exportPartnerIntegrationStatusSaga(
  action: ReturnType<typeof exportPartnerIntegrationStatusSagaAction>
) {
  try {
    yield put(integrationStatusSliceAction.setIntegrationStatusExportLoading(true));

    const response: AxiosResponse<Blob> = yield call(
      exportPartnerIntegrationStatusAPI,
      action.payload
    );

    const blob = response?.data;
    if (!blob || !(blob instanceof Blob)) {
      throw new Error("Invalid export response.");
    }

    const filename = getFileNameFromContentDisposition(
      response.headers?.["content-disposition"] ||
        response.headers?.["Content-Disposition"]
    );

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Integration status export failed:", error);
  } finally {
    yield put(integrationStatusSliceAction.setIntegrationStatusExportLoading(false));
  }
}

function* integrationStatusSaga() {
  yield takeLatest(
    integrationStatusSagaActionTypes.callGetPartnerIntegrationStatusSaga,
    getPartnerIntegrationStatusSaga
  );
  yield takeLatest(
    integrationStatusSagaActionTypes.callExportPartnerIntegrationStatusSaga,
    exportPartnerIntegrationStatusSaga
  );
}

export default integrationStatusSaga;
